import React, { useContext, useState, useEffect, useRef, useMemo } from 'react';
import { DatePicker, Checkbox, Icon, Select, Spin } from 'antd';
import moment from 'moment';
import { OrgContext } from "../../../context/OrgContext";
import { WarehouseContext } from "../../../context/WarehouseContext";
import { LineHaulApi } from "../../../api/LineHaulApi";
import { fetchRateSheet } from "../../../api/Billing";
import { losOptions as fetchSlLosOptions } from "../../../api/ServiceLevels";
import { alertMessage } from "../../../common/Common";
import { checkServiceExistance } from "../../../helpers/common";
import { getLinehaulRouteValidationError, isLinehaulOrderCode } from "../../../helpers/order_managemnt";
import Select_v2 from "../Select_v2";
import {
  buildLinehaulTariffPayload,
  extractLinehaulTariffLosId,
  resolveLinehaulSpecialFlag,
  resolveLinehaulTariffIdFromLosId,
  resolveTariffDropdownId,
  withAllStandardLos,
  isLinehaulTariffLockedByServiceLevel,
} from "../../../helpers/serviceLevelDerivation";
import { useModeBLinehaulTariffOptions } from "../../../helpers/useModeBLinehaulTariffOptions";
import {
  LinehaulTrackIcon,
  LhPillIcon,
  LhStopCompleteIcon,
  LhStopCurrentIcon,
  LhLegTruckKnobIcon,
  LhBadgeCompleteIcon,
  LhBadgeInTransitIcon,
  LhBadgePendingIcon,
  StarIcon,
  EditPennIcon,
  InfoIcon
} from "../../../common/CustomIcon";
import {
  getCompletedPrefixCodes,
  prefixLanePaths,
  composeRouteCodes,
  getRemainingRouteCodes,
  resolveCurrentLaneOriginId,
  attachCompletedPrefixToLane,
  withCurrentTrackingPath,
} from "./linehaulRemainingLanes";
import "./TrackingStyles.scss";

// Leg level status -> pill label, icon and colour matching Figma node 3689:102258
const LEG_STATUS = {
  pending: { label: "Pending", icon: LhBadgePendingIcon, color: "#1A354D" },
  arrived: { label: "Arrived", icon: LhBadgeCompleteIcon, color: "#CC9200" },
  in_transit: { label: "In Transit", icon: LhBadgeInTransitIcon, color: "#4559B0" },
  complete: { label: "Complete", icon: LhBadgeCompleteIcon, color: "#047857" }
};

// Backend statuses from linehaul_tracking_info
const STATUS_KEY = {
  "PENDING": "pending",
  "IN TRANSIT": "in_transit",
  "ARRIVED": "arrived",
  "COMPLETED": "complete",
  "ASSIGNED": "in_transit"
};

// Stop marker: icon + colour per stop state matching Figma specs
const STOP_MARKER = {
  complete: { Component: LhStopCompleteIcon, color: "#10B981" },
  arrived: { Component: LhStopCompleteIcon, color: "#FFBA07" },
  current: { Component: LhStopCurrentIcon, color: "#586FCC" },
  pending: { Component: LhPillIcon, color: "#8AA5B9" }
};

// Order labels that carry a customer pickup / delivery stop around the linehaul
const HAS_PICKUP = ["PLH", "PLHD"];
const HAS_DELIVERY = ["LHD", "PLHD"];
// Warehouse-to-warehouse (LH/NLH): no separate customer pickup/delivery legs.
// The order's PICKUP/DELIVERY locations are origin/destination warehouses (ORN/DST).
// Tracking terminals with type_of_loc PICKUP/DELIVERY are those warehouses, not
// "Pickup"/"Delivery" customer stops — label them with warehouse_code instead.
export const WAREHOUSE_ONLY_LINEHAUL_LABELS = ["LH", "NLH"];

export const isWarehouseOnlyLinehaul = (orderLabel = "") =>
  WAREHOUSE_ONLY_LINEHAUL_LABELS.includes(orderLabel);

// Date pills only on LH routing legs (warehouse-to-warehouse hops from path.legs).
// Customer pickup/delivery bookend connectors are excluded for PLH/LHD/PLHD.
// LH/NLH timelines are warehouse-only, so every connector is an LH leg.
export const shouldShowLegDatePill = (
  index,
  stopCount,
  orderLabel = "",
  stops = [],
  routingLegCount = 0
) => {
  if (stopCount <= 1 || index < 0 || index >= stopCount - 1) return false;
  if (!isLinehaulOrderCode(orderLabel)) {
    return index > 0 && index < stopCount - 2;
  }

  if (isWarehouseOnlyLinehaul(orderLabel)) {
    const legCount = routingLegCount > 0 ? routingLegCount : stopCount - 1;
    return index < legCount;
  }

  const pickupBookend = hasPickupBookend(orderLabel, stops);
  const deliveryBookend = hasDeliveryBookend(orderLabel, stops);
  const legCount = routingLegCount > 0
    ? routingLegCount
    : Math.max(stopCount - 1 - (pickupBookend ? 1 : 0) - (deliveryBookend ? 1 : 0), 0);

  if (legCount <= 0) return false;

  const lhLegStartIndex = pickupBookend ? 1 : 0;
  const lhLegEndIndex = lhLegStartIndex + legCount - 1;
  return index >= lhLegStartIndex && index <= lhLegEndIndex;
};

const hasPickupBookend = (orderLabel, stops = []) =>
  HAS_PICKUP.includes(orderLabel) && String(stops[0]?.name || "") === "Pickup";

const hasDeliveryBookend = (orderLabel, stops = []) =>
  HAS_DELIVERY.includes(orderLabel) && String(stops[stops.length - 1]?.name || "") === "Delivery";

// Map a timeline connector index to the 1-based routing-leg key used in legDatesMap.
export const resolveTimelineLegDateKey = (timelineIndex, stops = [], orderLabel = "", routingLegCount = 0) => {
  if (!Array.isArray(stops) || timelineIndex < 0 || timelineIndex >= stops.length - 1) {
    return null;
  }
  const pickupBookend = hasPickupBookend(orderLabel, stops);
  const deliveryBookend = hasDeliveryBookend(orderLabel, stops);
  const legCount = routingLegCount > 0
    ? routingLegCount
    : Math.max(stops.length - 1 - (pickupBookend ? 1 : 0) - (deliveryBookend ? 1 : 0), 1);

  if (pickupBookend && !deliveryBookend) {
    // PLH: LH legs start after the pickup bookend connector.
    if (timelineIndex === 0) return null;
    return Math.min(timelineIndex, legCount);
  }
  if (deliveryBookend && !pickupBookend) {
    // LHD: LH legs exclude the final hop into delivery.
    if (timelineIndex === stops.length - 2) return null;
    return Math.min(timelineIndex + 1, legCount);
  }
  if (pickupBookend && deliveryBookend) {
    // PLHD: only warehouse-to-warehouse LH legs between bookends.
    if (timelineIndex === 0 || timelineIndex === stops.length - 2) return null;
    return Math.min(timelineIndex, legCount);
  }
  // LH / NLH: warehouse-only timeline.
  return timelineIndex + 1;
};

const formatEta = (dateString, fallback = "N/A") => {
  if (!dateString) return fallback;
  const m = moment.parseZone(dateString);
  return m.isValid() ? m.format("MMM DD h:mm A") : fallback;
};

const formatTimeOnly = (dateString, fallback = "N/A") => {
  if (!dateString) return fallback;
  const m = moment.parseZone(dateString);
  return m.isValid() ? m.format("h:mm A") : fallback;
};

const formatDistance = (val) => {
  if (val === null || val === undefined || val === "") return "";
  if (typeof val === "number") return `${val} mi`;
  const str = String(val).trim();
  if (str.toLowerCase().includes("mi") || str.toLowerCase().includes("mile")) return str;
  return `${str} mi`;
};

const unwrapTrackingPayload = (res) => {
  if (!res || typeof res !== "object") return null;
  const nested = res.data;
  if (nested && typeof nested === "object" && (
    Array.isArray(nested.terminals)
    || nested.order_id
    || nested.lh_routing_path_id
  )) {
    return nested;
  }
  if (Array.isArray(res.terminals) || res.order_id || res.lh_routing_path_id) {
    return res;
  }
  return null;
};

const terminalWarehouseLabel = (terminal) =>
  terminal?.warehouse_code || terminal?.warehouse_name || terminal?.warehouse_city || "N/A";

const terminalStopName = (terminal, orderLabel = "") => {
  const locTypeUpper = String(terminal?.type_of_loc || "").toUpperCase();
  if (isWarehouseOnlyLinehaul(orderLabel)) {
    return terminalWarehouseLabel(terminal);
  }
  if (locTypeUpper === "PICKUP") return "Pickup";
  if (locTypeUpper === "DELIVERY") return "Delivery";
  return terminalWarehouseLabel(terminal);
};

const resolveTerminalStopState = (trackingStatusUpper) => {
  if (trackingStatusUpper === "COMPLETED" || trackingStatusUpper === "COMPLETE") {
    return { state: "complete", legState: "complete" };
  }
  if (trackingStatusUpper === "ARRIVED") {
    return { state: "arrived", legState: "pending" };
  }
  if (
    trackingStatusUpper === "IN TRANSIT"
    || trackingStatusUpper === "IN_TRANSIT"
    || trackingStatusUpper === "ACTIVE"
  ) {
    return { state: "current", legState: "active" };
  }
  return { state: "pending", legState: "pending" };
};

// Line after stop i is the inbound hop to stop i+1.
// next pending → grey, in transit → blue, arrived/completed → green.
const applyInboundConnectorColors = (stops = []) => {
  const incomingDistances = stops.map((stop) => stop.leg?.distance || "");
  for (let i = 0; i < stops.length; i += 1) {
    if (!stops[i].leg) continue;
    if (i < stops.length - 1) {
      const nextStop = stops[i + 1];
      stops[i].leg.distance = incomingDistances[i + 1];
      if (nextStop.state === "complete" || nextStop.state === "arrived") {
        stops[i].leg.state = "complete";
        stops[i].leg.progress = 0;
      } else if (nextStop.state === "current") {
        stops[i].leg.state = "active";
        stops[i].leg.progress = 0.5;
      } else {
        stops[i].leg.state = "pending";
        stops[i].leg.progress = 0;
      }
    } else {
      stops[i].leg.distance = "";
      stops[i].leg.state = "pending";
      stops[i].leg.progress = 0;
    }
  }
  return stops;
};

/**
 * Builds the timeline stops from linehaul_tracking_info or real-time terminals tracking data.
 * Supports legacy route_lane_name as well as terminals array from API v1/linehaul/tracking.
 */
export const buildStops = (data = {}, orderLabel = "") => {
  if (Array.isArray(data.stops_data) && data.stops_data.length > 0) {
    return data.stops_data;
  }

  // Support for new real-time API response structure with data.terminals array
  if (Array.isArray(data.terminals) && data.terminals.length > 0) {
    const sortedTerminals = [...data.terminals]
      .sort((a, b) => (a.sequence_no ?? 0) - (b.sequence_no ?? 0))
      .filter((t) => {
        if (isWarehouseOnlyLinehaul(orderLabel)) return true;
        const locTypeUpper = String(t?.type_of_loc || "").toUpperCase();
        if (locTypeUpper === "PICKUP") return HAS_PICKUP.includes(orderLabel);
        if (locTypeUpper === "DELIVERY") return HAS_DELIVERY.includes(orderLabel);
        return true;
      });

    const terminalStops = sortedTerminals.map((t, index) => {
      const code = terminalStopName(t, orderLabel);
      const locTypeUpper = String(t.type_of_loc || "").toUpperCase();
      const isCustomerStop = locTypeUpper === "PICKUP" || locTypeUpper === "DELIVERY";
      const trackingStatusUpper = String(t.tracking_status || t.status || "").toUpperCase();
      const { state, legState } = resolveTerminalStopState(trackingStatusUpper);

      let primary = "";
      if (state === "complete") {
        const depDate = t.actual_departure || t.departure_date || t.departure_time;
        const arrDate = t.actual_arrival || t.arrival_date || t.arrival_time;
        primary = depDate
          ? `DEP ${formatEta(depDate)}`
          : arrDate
          ? `ARR ${formatEta(arrDate)}`
          : isCustomerStop
          ? "N/A"
          : "";
      } else if (state === "arrived") {
        const arrDate = t.actual_arrival || t.arrival_date || t.arrival_time;
        primary = arrDate ? `ARR ${formatEta(arrDate)}` : (isCustomerStop ? "N/A" : "");
      } else if (state === "current") {
        primary = t.eta ? `ETA ${formatEta(t.eta)}` : (isCustomerStop ? "N/A" : "");
      } else {
        primary = t.eta ? `ETA ${formatEta(t.eta)}` : (isCustomerStop ? "N/A" : "");
      }

      let secondary = "";
      if (t.actual_arrival && t.actual_departure) {
        secondary = `${formatTimeOnly(t.actual_arrival)}→${formatTimeOnly(t.actual_departure)}`;
      } else if (t.etd) {
        secondary = `ETD ${formatEta(t.etd)}`;
      }

      const isCurrentLeg = legState === "active";
      const rawDistance = t.distance_from_previous ?? t.distance ?? t.distance_in_miles ?? t.miles ?? null;
      const legDistance = formatDistance(rawDistance);

      return {
        id: t.warehouse_id || `term-${index}`,
        name: code,
        state,
        primary,
        secondary,
        leg: {
          state: legState,
          progress: isCurrentLeg ? (t.leg_progress ?? 0.5) : 0,
          distance: legDistance
        }
      };
    });

    // Check if PICKUP or DELIVERY terminals exist in sortedTerminals
    const hasPickupTerm = sortedTerminals.some((t) => String(t.type_of_loc || "").toUpperCase() === "PICKUP");
    const hasDeliveryTerm = sortedTerminals.some((t) => String(t.type_of_loc || "").toUpperCase() === "DELIVERY");

    const firstTermState = terminalStops[0]?.state || "pending";
    const prependedPickupState =
      firstTermState === "complete"
        ? "complete"
        : firstTermState === "current" || firstTermState === "arrived"
        ? "current"
        : "pending";
    const prependedPickupLegState =
      firstTermState === "complete"
        ? "complete"
        : firstTermState === "current" || firstTermState === "arrived"
        ? "active"
        : "pending";

    const stops = [
      ...(!hasPickupTerm && HAS_PICKUP.includes(orderLabel)
        ? [
            {
              name: "Pickup",
              state: prependedPickupState,
              primary: data.pickup_date ? `DEP ${formatEta(data.pickup_date)}` : "N/A",
              secondary: "",
              leg: {
                state: prependedPickupLegState,
                progress: prependedPickupLegState === "active" ? 0.5 : 0,
                distance: ""
              }
            }
          ]
        : []),
      ...terminalStops,
      ...(!hasDeliveryTerm && HAS_DELIVERY.includes(orderLabel)
        ? [
            {
              name: "Delivery",
              state: terminalStops[terminalStops.length - 1]?.state === "complete" ? "complete" : "pending",
              primary: data.delivery_date ? `ETA ${formatEta(data.delivery_date)}` : "N/A",
              secondary: "",
              leg: { state: "pending", progress: 0, distance: "" }
            }
          ]
        : [])
    ];

    return applyInboundConnectorColors(stops);
  }

  const codes = String(data.route_lane_name || data.route_name || "")
    .split("-")
    .map((code) => code.trim())
    .filter(Boolean);

  if (codes.length === 0) return [];

  const names = [
    ...(HAS_PICKUP.includes(orderLabel) ? ["Pickup"] : []),
    ...codes,
    ...(HAS_DELIVERY.includes(orderLabel) ? ["Delivery"] : [])
  ];

  const statusKeyRaw = String(data.current_lh_status || data.status || "").toUpperCase();
  const status = STATUS_KEY[statusKeyRaw] || data.status?.toLowerCase() || "pending";
  const currentLocation = data.current_location;

  const currentIndex = status === "in_transit" || status === "arrived"
    ? names.findIndex((name) => name === currentLocation)
    : status === "complete"
    ? names.length - 1
    : -1;

  const stops = names.map((name, index) => {
    let state = "pending";
    if (status === "complete") {
      state = "complete";
    } else if (status === "arrived" && currentIndex >= 0 && index <= currentIndex) {
      state = index === currentIndex ? "arrived" : "complete";
    } else if (currentIndex >= 0) {
      if (index < currentIndex) state = "complete";
      else if (index === currentIndex) state = "current";
    }

    let primary = "";
    if (state === "complete") {
      primary = data.completed_dates?.[name]
        ? `DEP ${formatEta(data.completed_dates[name])}`
        : (data.date ? `DEP ${formatEta(data.date)}` : "DEP N/A");
    } else if (state === "arrived") {
      primary = data.date ? `ARR ${formatEta(data.date)}` : "ARR N/A";
    } else if (state === "current") {
      primary = data.date ? `ETA ${formatEta(data.date)}` : "ETA N/A";
    } else if (index === names.length - 1 && data.delivery_date) {
      primary = `ETA ${formatEta(data.delivery_date)}`;
    }

    return {
      name,
      state,
      primary,
      secondary: data.secondary_times?.[name] || "",
      leg: {
        state: "pending",
        progress: 0,
        distance: data.distances?.[index] ? formatDistance(data.distances[index]) : ""
      }
    };
  });

  return applyInboundConnectorColors(stops);
};

const DAY_MAP = {
  MON: "MONDAY", MONDAY: "MONDAY",
  TUE: "TUESDAY", TUES: "TUESDAY", TUESDAY: "TUESDAY",
  WED: "WEDNESDAY", WEDNESDAY: "WEDNESDAY",
  THU: "THURSDAY", THUR: "THURSDAY", THURS: "THURSDAY", THURSDAY: "THURSDAY",
  FRI: "FRIDAY", FRIDAY: "FRIDAY",
  SAT: "SATURDAY", SATURDAY: "SATURDAY",
  SUN: "SUNDAY", SUNDAY: "SUNDAY"
};

const normalizeOpDays = (days = []) => {
  if (!Array.isArray(days)) return [];
  return days
    .map((d) => DAY_MAP[String(d).trim().toUpperCase()] || String(d).trim().toUpperCase())
    .filter(Boolean);
};

export const resolvePickupAppointmentDate = (pickupLoc, pickupDateProp) => {
  const appointment = pickupLoc?.appointment;
  if (appointment && Object.prototype.hasOwnProperty.call(appointment, "appt_date")) {
    return appointment.appt_date || null;
  }
  return pickupDateProp || appointment?.appt_date || pickupLoc?.appointments?.[0]?.appt_date || null;
};

export const legHasOperationalDays = (leg = {}) =>
  normalizeOpDays(leg?.operational_days || []).length > 0;

// Backend encodes warehouse calendar dates as ISO (e.g. 2026-09-28T04:00:00Z).
// Always read the YYYY-MM-DD prefix — never shift via browser timezone.
export const extractCalendarYmd = (value) => {
  if (!value) return null;
  if (typeof value === "string") {
    const ymd = value.match(/^(\d{4}-\d{2}-\d{2})/);
    return ymd ? ymd[1] : null;
  }
  if (moment.isMoment(value) && value.isValid()) {
    return value.format("YYYY-MM-DD");
  }
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    const ymd = moment(value).format("YYYY-MM-DD");
    return ymd !== "Invalid date" ? ymd : null;
  }
  const parsed = moment.parseZone(value);
  return parsed.isValid() ? parsed.format("YYYY-MM-DD") : null;
};

const toStartOfDay = (value) => {
  const ymd = extractCalendarYmd(value);
  if (!ymd) return null;
  const parsed = moment(ymd, "YYYY-MM-DD", true);
  return parsed.isValid() ? parsed.startOf("day") : null;
};

export const formatCalendarDisplay = (value) => {
  const ymd = extractCalendarYmd(value);
  if (!ymd) return "Select Date";
  return moment(ymd, "YYYY-MM-DD", true).format("MMM DD");
};

const isCustomerBookendTerminal = (terminal) => {
  const locTypeUpper = String(terminal?.type_of_loc || "").toUpperCase();
  return locTypeUpper === "PICKUP" || locTypeUpper === "DELIVERY";
};

// PLH/PLHD tracking payloads include customer PICKUP/DELIVERY rows in terminals.
// LH leg N runs between warehouse-only stops — exclude bookends before indexing.
export const filterLhWarehouseTerminals = (terminals = []) => (
  [...(Array.isArray(terminals) ? terminals : [])]
    .sort((a, b) => (a.sequence_no ?? 0) - (b.sequence_no ?? 0))
    .filter((terminal) => !isCustomerBookendTerminal(terminal))
);

// Leg N departs warehouse[N-1]. Prefer the saved leg date, then origin warehouse date.
export const resolveLegLinehaulServiceDate = (
  sortedTerminals = [],
  legDateKey,
  legData = null
) => {
  if (!legDateKey || legDateKey < 1) return null;
  const warehouses = filterLhWarehouseTerminals(sortedTerminals);
  const originIdx = legDateKey - 1;
  const destIdx = legDateKey;
  return legData?.linehaul_service_date
    || warehouses[originIdx]?.linehaul_service_date
    || warehouses[destIdx]?.linehaul_service_date
    || null;
};

const getDateFloor = (...candidates) => {
  const today = moment().startOf("day");
  let floor = today.clone();
  candidates.forEach((candidate) => {
    const parsed = toStartOfDay(candidate);
    if (parsed && parsed.isAfter(floor)) {
      floor = parsed;
    }
  });
  return floor;
};

const getNextOperationalDate = (startMoment, operationalDays = []) => {
  const base = toStartOfDay(startMoment) || moment().startOf("day");
  const upperOpDays = normalizeOpDays(operationalDays);
  if (upperOpDays.length === 0) {
    return base;
  }
  let curr = base.clone();
  for (let i = 0; i < 60; i += 1) {
    const dayOfWeek = curr.format("dddd").toUpperCase();
    if (upperOpDays.includes(dayOfWeek)) {
      return curr;
    }
    curr.add(1, "day");
  }
  return base;
};

// First leg default from pickup: next operational day on/after pickup, or pickup itself when leg has none.
export const resolveFirstLegDateFromPickup = (pickupDate, firstLeg = {}) => {
  const pickup = toStartOfDay(pickupDate);
  if (!pickup) return null;
  if (legHasOperationalDays(firstLeg)) {
    return getNextOperationalDate(pickup, firstLeg.operational_days || []);
  }
  return pickup.clone();
};

// Leg service dates cannot be before pickup (fallback to today when pickup is unset).
export const resolveLegDateMinSelectable = (pickupDate) => {
  const pickup = toStartOfDay(pickupDate);
  return pickup || moment().startOf("day");
};

const LegDatePillPicker = ({ value, onChange, disabled, operationalDays = [], minDate }) => {
  const [open, setOpen] = useState(false);

  const calendarValue = toStartOfDay(value);
  const displayDate = formatCalendarDisplay(value);
  const minSelectable = resolveLegDateMinSelectable(minDate);

  // Ant Design renders the calendar on document.body. Parent views scroll an
  // inner container, so the popup would otherwise stay on screen while the
  // date pill moves. Close it on any ancestor scroll.
  useEffect(() => {
    if (!open) return undefined;

    const closeOnScroll = (event) => {
      if (event.target?.closest?.(".lh-calendar-popover")) {
        return;
      }
      setOpen(false);
    };

    window.addEventListener("scroll", closeOnScroll, true);
    return () => window.removeEventListener("scroll", closeOnScroll, true);
  }, [open]);

  if (disabled) {
    return (
      <span className="lh-leg-date-pill is-disabled">
        <Icon type="calendar" style={{ fontSize: "11px", color: "#AABDCD" }} />
        <span>{displayDate}</span>
      </span>
    );
  }

  const upperOpDays = normalizeOpDays(operationalDays);
  const hasNoOpDays = upperOpDays.length === 0;

  const disabledDate = (currentDate) => {
    if (!currentDate) return false;
    return currentDate.isBefore(minSelectable, "day");
  };

  const dateRender = (currentDate) => {
    if (!currentDate) return null;
    const isBeforeMin = currentDate.isBefore(minSelectable, "day");
    const dayOfWeek = currentDate.format("dddd").toUpperCase();
    const isOperational = !hasNoOpDays && !isBeforeMin && upperOpDays.includes(dayOfWeek);
    const isSelected = calendarValue && currentDate.isSame(calendarValue, "day");

    let dayClass = "lh-cal-day";
    if (isSelected) {
      dayClass += " is-selected";
    } else if (isOperational) {
      dayClass += " is-operational";
    } else if (isBeforeMin) {
      dayClass += " is-disabled";
    }

    return (
      <div className="lh-cal-date">
        <span className={dayClass}>
          {currentDate.date()}
        </span>
      </div>
    );
  };

  return (
    <div className="lh-leg-date-pill-wrap" style={{ position: "relative", display: "inline-block" }}>
      <button
        type="button"
        className="lh-leg-date-pill"
        onClick={() => setOpen(true)}
      >
        <Icon type="calendar" style={{ fontSize: "11px", color: "#8AA5B9" }} />
        <span>{displayDate}</span>
      </button>

      <DatePicker
        open={open}
        onOpenChange={(status) => setOpen(status)}
        value={calendarValue}
        onChange={(date) => {
          if (date) onChange(toStartOfDay(date) || date);
          setOpen(false);
        }}
        disabledDate={disabledDate}
        dateRender={dateRender}
        defaultPickerValue={calendarValue || minSelectable}
        showToday={false}
        renderExtraFooter={hasNoOpDays ? () => (
          <div className="lh-cal-no-op-days">
            <Icon type="info-circle" />
            <span>No operational days for this leg</span>
          </div>
        ) : undefined}
        dropdownClassName={`lh-calendar-popover${hasNoOpDays ? " has-no-op-days" : ""}`}
        format="MM/DD/YYYY"
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: 0,
          height: 0,
          opacity: 0,
          pointerEvents: "none"
        }}
      />
    </div>
  );
};

export const LegConnector = ({ leg = {}, datePill }) => {
  const { state = "pending", distance } = leg;

  return (
    <div className="lh-leg">
      <div className={`lh-leg-bar lh-leg-bar-${state}`}>
        {state === "active" && (
          <span className="lh-leg-knob">
            <LhLegTruckKnobIcon />
          </span>
        )}
      </div>
      {distance && <span className="lh-leg-distance">{distance}</span>}
      {datePill && (
        <div className="lh-leg-date-pill-wrap">
          {datePill}
        </div>
      )}
    </div>
  );
};

export const Stop = ({ stop, align }) => {
  const { name, state = "pending", primary, secondary } = stop;
  const { Component: Marker, color } = STOP_MARKER[state] || STOP_MARKER.pending;

  return (
    <div className={`lh-stop lh-stop-${align}`}>
      <span className="lh-stop-marker">
        <Marker color={color} width="24" height="24" />
      </span>
      <span className={`lh-stop-name lh-stop-name-${state}`}>{name}</span>
      {primary && <span className="lh-stop-primary">{primary}</span>}
      {secondary && <span className="lh-stop-secondary">{secondary}</span>}
    </div>
  );
};

const transformPaths = (rawPaths = [], warehouseHash = {}) => {
  return (Array.isArray(rawPaths) ? rawPaths : []).map((path) => {
    let routeCodes = [];
    if (Array.isArray(path.legs) && path.legs.length > 0) {
      routeCodes = [path.legs[0].origin_code, ...path.legs.map((l) => l.destination_code)];
    } else if (Array.isArray(path.stops) && path.stops.length > 0) {
      routeCodes = path.stops.map((stopId) => warehouseHash[stopId]?.code || warehouseHash[stopId]?.name || stopId);
    } else {
      routeCodes = String(path.path_name || path.route || "").split("-").map((c) => c.trim());
    }

    return {
      path_id: path.path_id || path._id || path.id,
      route: path.path_name || routeCodes.filter(Boolean).join("-"),
      routeCodes,
      is_primary: Boolean(path.is_primary),
      stops: path.stops || routeCodes,
      legs: path.legs || []
    };
  }).filter((path) => path.path_id);
};

const CUSTOMER_STOP_NAMES = new Set(["Pickup", "Delivery"]);

export const getCompletedRouteCodes = (stops = []) => {
  const codes = [];
  for (const stop of (Array.isArray(stops) ? stops : [])) {
    if (!stop || CUSTOMER_STOP_NAMES.has(stop.name)) continue;
    if (stop.state !== "complete") break;
    const code = String(stop.name || "").trim();
    if (code) codes.push(code);
  }
  return codes;
};

export const isLastRoutingLegComplete = (stops = []) => {
  const routingStops = (Array.isArray(stops) ? stops : []).filter(
    (stop) => stop && !CUSTOMER_STOP_NAMES.has(stop.name)
  );
  if (!routingStops.length) return false;
  return routingStops[routingStops.length - 1].state === "complete";
};

export const isLegDateLocked = (originStop) => originStop?.state === "complete";

export const renderRoutingPathLabel = (route = "", completedCodes = []) => {
  const parts = composeRouteCodes(route, completedCodes);
  if (!parts.length) return route || "";

  let completedCount = 0;
  while (
    completedCount < parts.length
    && completedCount < completedCodes.length
    && parts[completedCount].toUpperCase() === String(completedCodes[completedCount]).toUpperCase()
  ) {
    completedCount += 1;
  }

  if (completedCount === 0) return parts.join("-") || route || "";

  const completedText = parts.slice(0, completedCount).join("-")
    + (completedCount < parts.length ? "-" : "");
  const remainingText = parts.slice(completedCount).join("-");

  return (
    <span className="lh-path-label">
      <span className="lh-path-label-completed">{completedText}</span>
      {remainingText ? <span className="lh-path-label-active">{remainingText}</span> : null}
    </span>
  );
};

const resolveTrackingPathId = (tracking, fallbackPathId) =>
  tracking?.lh_routing_path_id || tracking?.path_id || fallbackPathId || null;

export const buildStopsFromLane = (lane, orderLabel = "") => {
  if (!lane) return [];
  const stopCodes = Array.isArray(lane.routeCodes) && lane.routeCodes.length
    ? lane.routeCodes.filter(Boolean)
    : String(lane.route || "").split("-").map((s) => s.trim()).filter(Boolean);
  if (!stopCodes.length) return [];

  const names = [
    ...(HAS_PICKUP.includes(orderLabel) ? ["Pickup"] : []),
    ...stopCodes,
    ...(HAS_DELIVERY.includes(orderLabel) ? ["Delivery"] : [])
  ];

  return names.map((name) => ({
    name,
    state: "pending",
    primary: name === "Pickup" || name === "Delivery" ? "N/A" : "",
    secondary: "",
    leg: { state: "pending", progress: 0, distance: "" }
  }));
};

const namesMatch = (left, right) =>
  String(left || "").trim().toUpperCase() === String(right || "").trim().toUpperCase();

export const overlayTrackingOnLaneStops = (laneStops = [], trackingStops = []) => {
  if (!Array.isArray(laneStops) || laneStops.length === 0) return laneStops;
  const tracked = Array.isArray(trackingStops) ? trackingStops : [];
  let trackIndex = 0;
  let diverged = false;

  const copyStatus = (stop, match) => ({
    ...stop,
    state: match.state != null ? match.state : stop.state,
    primary: match.primary || stop.primary,
    secondary: match.secondary || stop.secondary,
    leg: {
      ...(stop.leg || {}),
      ...(match.leg || {})
    }
  });

  return laneStops.map((stop) => {
    if (diverged) return stop;

    while (
      trackIndex < tracked.length
      && CUSTOMER_STOP_NAMES.has(tracked[trackIndex].name)
      && !namesMatch(tracked[trackIndex].name, stop.name)
    ) {
      trackIndex += 1;
    }

    if (CUSTOMER_STOP_NAMES.has(stop.name)) {
      const match = tracked[trackIndex];
      if (match && namesMatch(match.name, stop.name)) {
        trackIndex += 1;
        return copyStatus(stop, match);
      }
      return stop;
    }

    const match = tracked[trackIndex];
    if (!match || !namesMatch(match.name, stop.name)) {
      diverged = true;
      return stop;
    }
    trackIndex += 1;
    return copyStatus(stop, match);
  });
};

const formatLegDateKey = (value) => {
  const parsed = toStartOfDay(value);
  return parsed ? parsed.format("YYYY-MM-DD") : null;
};

const buildLegsFromLegDatesMap = (datesMap = {}, terminals = []) => {
  const warehouses = filterLhWarehouseTerminals(terminals);
  const connectorKeys = Object.keys(datesMap)
    .map((key) => Number(key))
    .filter((key) => key > 0 && formatLegDateKey(datesMap[key]))
    .sort((a, b) => a - b);

  if (connectorKeys.length === 0) return [];

  return connectorKeys.map((connectorIndex) => {
    const legIdx = connectorIndex - 1;
    const originTerminal = warehouses[legIdx] || warehouses[connectorIndex - 1];
    const destTerminal = warehouses[connectorIndex] || warehouses[legIdx + 1];
    return {
      connectorIndex,
      routing_id: "",
      origin_id: originTerminal?.warehouse_id || destTerminal?.warehouse_id || "",
      origin_code: originTerminal?.warehouse_code || originTerminal?.warehouse_name || "",
      destination_id: destTerminal?.warehouse_id || "",
      destination_code: destTerminal?.warehouse_code || destTerminal?.warehouse_name || "",
    };
  });
};

const resolveLegsForSave = (activeLane, activeData, legDatesMap) => {
  if (Array.isArray(activeLane?.legs) && activeLane.legs.length > 0) {
    return activeLane.legs;
  }
  if (Array.isArray(activeData?.legs) && activeData.legs.length > 0) {
    return activeData.legs;
  }
  return buildLegsFromLegDatesMap(legDatesMap, activeData?.terminals);
};

const snapshotLegDateMap = (map = {}) => {
  const snapshot = {};
  Object.entries(map || {}).forEach(([key, value]) => {
    snapshot[String(key)] = formatLegDateKey(value);
  });
  return snapshot;
};

const buildLegDatesFromTerminals = (terminals = [], legs = []) => {
  const warehouses = filterLhWarehouseTerminals(terminals);
  const map = {};
  const count = Array.isArray(legs) && legs.length > 0 ? legs.length : Math.max(warehouses.length - 1, 0);
  for (let idx = 0; idx < count; idx += 1) {
    const connectorIndex = idx + 1;
    const legData = Array.isArray(legs) ? legs[idx] : null;
    const dateVal = resolveLegLinehaulServiceDate(warehouses, connectorIndex, legData);
    if (!dateVal) continue;
    const parsed = toStartOfDay(dateVal);
    if (parsed) map[connectorIndex] = parsed;
  }
  return map;
};

export const LhTrackingCard = ({
  data = {},
  orderLabel = "",
  title = "Linehaul",
  onViewTracking,
  stops: stopsProp,
  currentPathId,
  originZipcode = "",
  destinationZipcode = "",
  pickupWarehouseId,
  deliveryWarehouseId,
  customerOrderId,
  levelOfServices = [],
  standardLosList = [],
  linehaulLosOptions: linehaulLosOptionsProp = [],
  serviceLevelLegLos = null,
  disableTariffEdit = false,
  currentOrder = {},
  accountBillingConfig = {},
  orgBillingRules = {},
}) => {
  const { currentOrg, orgSettings = {} } = useContext(OrgContext);
  const { warehouseHash } = useContext(WarehouseContext);

  const [collapsed, setCollapsed] = useState(false);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savingTip, setSavingTip] = useState("Saving routing path...");
  const trackingRefreshTimerRef = useRef(null);
  const [fetchingLanes, setFetchingLanes] = useState(false);
  const [lanePaths, setLanePaths] = useState([]);
  const [orgStandardLos, setOrgStandardLos] = useState(() => (
    Array.isArray(standardLosList) ? standardLosList : []
  ));
  const [realTimeData, setRealTimeData] = useState(null);
  const [savedLinehaulTariffLosId, setSavedLinehaulTariffLosId] = useState(
    () => extractLinehaulTariffLosId(currentOrder) || null
  );
  const savedTariffLosIdRef = useRef(extractLinehaulTariffLosId(currentOrder));
  const savedTariffDropdownIdRef = useRef(null);
  const savedTariffMetaRef = useRef(null);
  const userTouchedTariffRef = useRef(false);
  const [tariffId, setTariffId] = useState(undefined);

  const [legDatesMap, setLegDatesMap] = useState({});
  const pickupLocation = (currentOrder?.locations || []).find((loc) => loc.type_of_loc === "PICKUP");
  const pickupDateMoment = toStartOfDay(
    resolvePickupAppointmentDate(
      pickupLocation,
      data?.pickup_date || currentOrder?.customer_order_details?.pickup_date
    )
  );
  const pickupDateKey = pickupDateMoment ? pickupDateMoment.format("YYYY-MM-DD") : "";
  const appliedApiLegDatesRef = useRef(false);
  const prevEditPathRef = useRef(undefined);
  const originalLegDatesRef = useRef({});
  const manualFirstLegEditRef = useRef(false);
  const lastPickupSyncedKeyRef = useRef(null);
  const prevPickupDateKeyRef = useRef(undefined);
  const initialPickupDateKeyRef = useRef(null);
  const lastLaneFetchKeyRef = useRef("");
  const laneFetchGenRef = useRef(0);
  const lanePathsRef = useRef([]);

  const fetchRealtimeTracking = () => {
    if (!customerOrderId) return Promise.resolve();
    return LineHaulApi.fetchLineHaulRealTimeTracking(customerOrderId)
      .then((res) => {
        const payload = unwrapTrackingPayload(res);
        setRealTimeData(payload);
      })
      .catch((err) => {
        console.error("Failed to fetch real-time linehaul tracking:", err);
        setRealTimeData(null);
      });
  };

  const waitForTrackingRefresh = (ms = 3000) => new Promise((resolve) => {
    if (trackingRefreshTimerRef.current) {
      clearTimeout(trackingRefreshTimerRef.current);
    }
    trackingRefreshTimerRef.current = setTimeout(() => {
      trackingRefreshTimerRef.current = null;
      resolve();
    }, ms);
  });

  useEffect(() => {
    fetchRealtimeTracking();
  }, [customerOrderId, originZipcode, destinationZipcode]);

  useEffect(() => () => {
    laneFetchGenRef.current += 1;
    if (trackingRefreshTimerRef.current) {
      clearTimeout(trackingRefreshTimerRef.current);
    }
  }, []);

  const activeData = realTimeData || data;
  const hasRealtimeStops = Boolean(
    (Array.isArray(activeData?.terminals) && activeData.terminals.length > 0)
    || (Array.isArray(activeData?.stops_data) && activeData.stops_data.length > 0)
  );

  // Form field states
  const [editPathId, setEditPathId] = useState(currentPathId || activeData.lh_routing_path_id || activeData.path_id || null);
  const [editServiceLeg, setEditServiceLeg] = useState(activeData.service_date_leg || null);
  const [editServiceDate, setEditServiceDate] = useState(
    activeData.service_date ? moment(activeData.service_date) : (activeData.date ? moment(activeData.date) : null)
  );
  const [savedLhSpecial, setSavedLhSpecial] = useState(null);
  const resolveLhSpecial = (savedSpecial = savedLhSpecial) => resolveLinehaulSpecialFlag({
    tracking: activeData,
    order: currentOrder,
    savedSpecial,
  });
  const [editLhSpecial, setEditLhSpecial] = useState(() => resolveLhSpecial(null));

  useEffect(() => {
    if (prevEditPathRef.current === undefined) {
      prevEditPathRef.current = editPathId;
    } else if (String(prevEditPathRef.current || "") !== String(editPathId || "")) {
      prevEditPathRef.current = editPathId;
      appliedApiLegDatesRef.current = false;
      manualFirstLegEditRef.current = false;
      lastPickupSyncedKeyRef.current = null;
      setLegDatesMap({});
      originalLegDatesRef.current = {};
    }
  }, [editPathId]);

  useEffect(() => {
    // Pickup-derived leg dates are edit-mode defaults only — view mode shows API data as-is.
    if (!editing) return;

    const activeLane = lanePaths.find((p) => String(p.path_id) === String(editPathId)) || lanePaths[0];
    const firstLeg = activeLane?.legs?.[0];
    if (!firstLeg) return;

    const isInitialRun = prevPickupDateKeyRef.current === undefined;
    const pickupChanged = !isInitialRun && prevPickupDateKeyRef.current !== pickupDateKey;
    prevPickupDateKeyRef.current = pickupDateKey;

    const savedLegs = activeData?.legs || [];
    const legsForDates = savedLegs.length > 0 ? savedLegs : activeLane?.legs || [];
    const terminalDates = buildLegDatesFromTerminals(activeData?.terminals, legsForDates);
    const hasSavedLeg1 = Boolean(
      terminalDates[1]
      || savedLegs[0]?.linehaul_service_date
      || originalLegDatesRef.current[1]
      || originalLegDatesRef.current["1"]
    );

    if (isInitialRun) {
      if (pickupDateKey && initialPickupDateKeyRef.current == null) {
        initialPickupDateKeyRef.current = pickupDateKey;
      }
    }

    if (hasSavedLeg1 && !pickupChanged) {
      if (pickupDateKey) lastPickupSyncedKeyRef.current = pickupDateKey;
      return;
    }

    const savedLeg1Key = originalLegDatesRef.current[1]
      ?? originalLegDatesRef.current["1"]
      ?? formatLegDateKey(terminalDates[1]);
    if (
      initialPickupDateKeyRef.current
      && pickupDateKey === initialPickupDateKeyRef.current
      && savedLeg1Key
    ) {
      manualFirstLegEditRef.current = false;
      lastPickupSyncedKeyRef.current = pickupDateKey;
      const restored = toStartOfDay(savedLeg1Key);
      if (restored) {
        setLegDatesMap((prev) => ({ ...prev, 1: restored }));
      }
      return;
    }

    if (pickupChanged) {
      manualFirstLegEditRef.current = false;
      lastPickupSyncedKeyRef.current = null;
    }

    if (manualFirstLegEditRef.current) return;

    if (!pickupDateMoment) {
      lastPickupSyncedKeyRef.current = null;
      setLegDatesMap((prev) => {
        if (!prev[1]) return prev;
        const next = { ...prev };
        delete next[1];
        return next;
      });
      return;
    }

    if (!pickupChanged && lastPickupSyncedKeyRef.current === pickupDateKey) return;

    const nextDate = resolveFirstLegDateFromPickup(pickupDateMoment, firstLeg);
    if (!nextDate) return;

    lastPickupSyncedKeyRef.current = pickupDateKey;
    setLegDatesMap((prev) => ({ ...prev, 1: nextDate }));
  }, [editing, editPathId, lanePaths, pickupDateKey, activeData?.terminals, activeData?.legs]);

  useEffect(() => {
    if (appliedApiLegDatesRef.current) return;

    const activeLane = lanePaths.find((p) => String(p.path_id) === String(editPathId)) || lanePaths[0];
    const routingLegs = activeLane?.legs || [];
    const savedLegs = activeData?.legs || [];
    const legsForDates = savedLegs.length > 0 ? savedLegs : routingLegs;
    const terminalDates = buildLegDatesFromTerminals(activeData?.terminals, legsForDates);

    if (Object.keys(terminalDates).length > 0) {
      appliedApiLegDatesRef.current = true;
      setLegDatesMap(terminalDates);
      originalLegDatesRef.current = snapshotLegDateMap(terminalDates);
    }
  }, [editPathId, lanePaths, activeData?.terminals, activeData?.legs]);

  const statusKeyRaw = String(activeData.current_lh_status || "").toUpperCase();
  const orderStatusRaw = String(activeData.status || "").toUpperCase();
  const status = STATUS_KEY[statusKeyRaw] || STATUS_KEY[orderStatusRaw] || activeData.status?.toLowerCase() || "pending";
  const { label, icon: PillIcon, color } = LEG_STATUS[status] || LEG_STATUS.pending;
  const selectedLane = lanePaths.find((p) => String(p.path_id) === String(editPathId)) || null;
  const trackingStops = stopsProp || (hasRealtimeStops ? buildStops(activeData, orderLabel) : []);
  // Keep current tracking on screen until Save. Remaining lanes live in the dropdown only.
  const stops = trackingStops.length
    ? trackingStops
    : (editing && selectedLane ? buildStopsFromLane(selectedLane, orderLabel) : []);
  const completedRouteCodes = getCompletedPrefixCodes(activeData?.terminals, orderLabel);
  const completedFromStops = getCompletedRouteCodes(trackingStops);
  const dropdownCompletedCodes = completedRouteCodes.length ? completedRouteCodes : completedFromStops;
  const lastRoutingLegComplete = isLastRoutingLegComplete(trackingStops);
  const displayLanePaths = prefixLanePaths(
    lanePaths.length ? lanePaths : transformPaths(activeData?.paths || [], warehouseHash),
    activeData?.terminals,
    orderLabel,
    dropdownCompletedCodes
  );
  const selectedPathReady = displayLanePaths.some((path) => String(path.path_id) === String(editPathId));
  const isLinehaulSpecial = resolveLhSpecial();

  // Generate leg segments for Service Date Leg dropdown / inline leg pills
  const routeCodes = Array.isArray(activeData.terminals) && activeData.terminals.length > 0
    ? activeData.terminals
        .map((t) => {
          const locTypeUpper = String(t.type_of_loc || "").toUpperCase();
          if (locTypeUpper === "PICKUP") return "Pickup";
          if (locTypeUpper === "DELIVERY") return "Delivery";
          return t.warehouse_code || t.warehouse_name || t.warehouse_city || "N/A";
        })
        .filter(Boolean)
    : String(activeData.route_lane_name || activeData.route_name || "")
        .split("-")
        .map((c) => c.trim())
        .filter(Boolean);

  const legOptions = [];
  if (routeCodes.length >= 2) {
    for (let i = 0; i < routeCodes.length - 1; i++) {
      const legLabel = `${routeCodes[i]}–${routeCodes[i + 1]}`;
      legOptions.push({ label: legLabel, value: legLabel });
    }
  } else if (routeCodes.length === 1) {
    legOptions.push({ label: routeCodes[0], value: routeCodes[0] });
  }

  useEffect(() => {
    if (Array.isArray(standardLosList) && standardLosList.length > 0) {
      setOrgStandardLos(standardLosList);
    }
  }, [standardLosList]);

  const serviceLevelFlowEnabled = orgSettings?.service_level_flow_enabled === "true";
  const baseLinehaulLosOptions = useMemo(
    () => (
      linehaulLosOptionsProp.length > 0
        ? linehaulLosOptionsProp
        : (serviceLevelFlowEnabled
          ? withAllStandardLos(levelOfServices, orgStandardLos)
          : levelOfServices)
    ),
    [linehaulLosOptionsProp, levelOfServices, orgStandardLos, serviceLevelFlowEnabled]
  );
  const {
    effectiveLosOptions: losOptions,
    modeBDefaultTariffId,
    modeBDefaultReady,
    effectiveLosOptionsKey: losOptionsKey,
  } = useModeBLinehaulTariffOptions({
    serviceLevelFlowEnabled,
    baseOptions: baseLinehaulLosOptions,
    accountBillingConfig,
    orgBillingRules,
    savedLinehaulTariffId: savedLinehaulTariffLosId,
  });
  const linehaulTariffEditLocked = disableTariffEdit || (
    orgSettings?.service_level_flow_enabled === "true"
    && !checkServiceExistance("OLOS")
    && isLinehaulTariffLockedByServiceLevel(serviceLevelLegLos, losOptions)
  );

  const resolveTariffForDropdown = () => resolveTariffDropdownId({
    losOptions,
    savedDropdownId: savedTariffDropdownIdRef.current,
    savedLosId: savedLinehaulTariffLosId ?? savedTariffLosIdRef.current,
    savedTariff: savedTariffMetaRef.current,
    order: currentOrder,
    // activeData is already realTimeData || currentOrder.linehaul_tracking_info, and the
    // Rails tracking hash never carries tariff/los_code — only the Java payload can.
    tracking: activeData,
  });

  useEffect(() => {
    const fromOrder = extractLinehaulTariffLosId(currentOrder);
    if (fromOrder) {
      savedTariffLosIdRef.current = fromOrder;
      setSavedLinehaulTariffLosId(fromOrder);
    }
  }, [
    currentOrder?.linehaul_tariff_id,
    currentOrder?.customer_order_details?.linehaul_tariff_id,
  ]);

  useEffect(() => {
    if (serviceLevelFlowEnabled && !losOptions.length) return;
    // While editing, only auto-fill until the user picks a tariff themselves.
    if (editing && userTouchedTariffRef.current) return;

    if (!savedTariffDropdownIdRef.current && savedTariffLosIdRef.current) {
      const mapped = resolveLinehaulTariffIdFromLosId(savedTariffLosIdRef.current, losOptions);
      if (mapped) savedTariffDropdownIdRef.current = mapped;
    }

    const hasSavedTariff = Boolean(
      savedTariffDropdownIdRef.current
      || savedTariffLosIdRef.current
      || savedLinehaulTariffLosId
      || extractLinehaulTariffLosId(currentOrder)
    );

    if (serviceLevelFlowEnabled) {
      const nextTariffId = resolveTariffForDropdown()
        || (savedTariffLosIdRef.current ? String(savedTariffLosIdRef.current) : undefined);
      if (nextTariffId) {
        setTariffId(nextTariffId);
      } else if (!hasSavedTariff && !editing) {
        setTariffId(undefined);
      }
      return;
    }

    if (hasSavedTariff) {
      const nextTariffId = resolveTariffForDropdown()
        || (savedTariffLosIdRef.current
          ? resolveLinehaulTariffIdFromLosId(savedTariffLosIdRef.current, losOptions)
          : undefined)
        || (savedTariffLosIdRef.current ? String(savedTariffLosIdRef.current) : undefined);
      if (nextTariffId) {
        setTariffId(nextTariffId);
      }
      return;
    }

    if (editing && modeBDefaultReady && modeBDefaultTariffId) {
      setTariffId(String(modeBDefaultTariffId));
    } else if (!editing) {
      setTariffId(undefined);
    }
  }, [
    losOptionsKey,
    activeData?.tariff,
    activeData?.los_code,
    editing,
    savedLinehaulTariffLosId,
    currentOrder?.linehaul_tariff_id,
    currentOrder?.customer_order_details?.linehaul_tariff_id,
    serviceLevelFlowEnabled,
    modeBDefaultReady,
    modeBDefaultTariffId,
    accountBillingConfig,
  ]);

  useEffect(() => {
    if (editing) return;
    const pathId = resolveTrackingPathId(activeData, currentPathId);
    if (pathId) setEditPathId(pathId);
  }, [activeData?.lh_routing_path_id, currentPathId, editing]);

  const resolveWarehouseIds = async () => {
    const pickupLoc = currentOrder?.locations?.find((loc) => loc.type_of_loc === "PICKUP");
    const deliveryLoc = currentOrder?.locations?.find((loc) => loc.type_of_loc === "DELIVERY");
    const destId = deliveryWarehouseId || deliveryLoc?.warehouse_id;
    const fallbackOrigin = pickupWarehouseId || pickupLoc?.warehouse_id;
    const originId = resolveCurrentLaneOriginId(
      activeData?.terminals,
      fallbackOrigin,
      warehouseHash,
      orderLabel
    );
    if (originId && destId) {
      return { originId, destId };
    }

    const originZip = originZipcode || pickupLoc?.address?.zipcode || pickupLoc?.customer_zipcode;
    const destZip = destinationZipcode || deliveryLoc?.address?.zipcode || deliveryLoc?.customer_zipcode;
    if (!currentOrg?.id || !originZip || !destZip) return null;

    const warehouses = await LineHaulApi.fetchWarehousesByZipcode(
      currentOrg.id,
      originZip,
      destZip
    );
    if (!warehouses.success) return null;

    const { origin_warehouse, destination_warehouse } = warehouses.data || {};
    if (!origin_warehouse || !destination_warehouse) return null;
    return {
      originId: originId || origin_warehouse,
      destId: destId || destination_warehouse,
    };
  };

  const applyLanePaths = (rawPaths = []) => {
    const remainingCodes = getRemainingRouteCodes(activeData?.terminals, orderLabel);
    const fallbackPrefix = getCompletedRouteCodes(trackingStops);
    const prefixed = prefixLanePaths(rawPaths, activeData?.terminals, orderLabel, fallbackPrefix);
    const filtered = prefixed.filter((path) => {
      const start = (path.remainingCodes || [])[0];
      if (!remainingCodes[0] || !start) return true;
      return String(start).toUpperCase() === String(remainingCodes[0]).toUpperCase();
    });
    const { paths, preferred } = withCurrentTrackingPath(
      filtered.length ? filtered : prefixed,
      activeData?.terminals,
      orderLabel,
      resolveTrackingPathId(activeData, currentPathId)
    );
    setLanePaths(paths);
    lanePathsRef.current = paths;
    setEditPathId(preferred?.path_id ? String(preferred.path_id) : null);
    return paths;
  };

  useEffect(() => {
    if (!editing || !lanePathsRef.current.length) return;
    const next = prefixLanePaths(
      lanePathsRef.current,
      activeData?.terminals,
      orderLabel,
      getCompletedRouteCodes(trackingStops)
    );
    const unchanged = next.every((path, idx) => path.route === lanePathsRef.current[idx]?.route);
    if (unchanged) return;
    lanePathsRef.current = next;
    setLanePaths(next);
  }, [activeData?.terminals, orderLabel, editing, trackingStops]);

  // Lanes are only needed when the user opens the editor.
  const fetchLanes = async () => {
    if (!currentOrg?.id) return;
    const gen = laneFetchGenRef.current + 1;
    laneFetchGenRef.current = gen;
    setFetchingLanes(true);
    try {
      const ids = await resolveWarehouseIds();
      if (gen !== laneFetchGenRef.current) return;
      if (!ids) {
        setLanePaths([]);
        lanePathsRef.current = [];
        return;
      }

      // Last hop: origin is already the delivery warehouse. No remaining lane to fetch.
      if (String(ids.originId) === String(ids.destId)) {
        applyLanePaths([]);
        return;
      }

      const fetchKey = `${currentOrg.id}|${ids.originId}|${ids.destId}`;
      if (lastLaneFetchKeyRef.current === fetchKey && lanePathsRef.current.length > 0) {
        applyLanePaths(lanePathsRef.current);
        return;
      }

      const result = await LineHaulApi.fetchLineHaulLanesJava(currentOrg.id, ids.originId, ids.destId);
      if (gen !== laneFetchGenRef.current) return;
      if (!result.success || !Array.isArray(result.data?.paths)) {
        lastLaneFetchKeyRef.current = "";
        applyLanePaths([]);
        if (result?.message) alertMessage(result.message, "warning");
        return;
      }

      lastLaneFetchKeyRef.current = fetchKey;
      const prefixCodes = getCompletedPrefixCodes(activeData?.terminals, orderLabel);
      applyLanePaths(
        transformPaths(result.data.paths, warehouseHash)
          .map((path) => attachCompletedPrefixToLane(path, prefixCodes))
      );
    } catch (err) {
      if (gen !== laneFetchGenRef.current) return;
      console.error("Failed to fetch linehaul routing paths:", err);
      lastLaneFetchKeyRef.current = "";
      setLanePaths([]);
      lanePathsRef.current = [];
    } finally {
      if (gen === laneFetchGenRef.current) setFetchingLanes(false);
    }
  };

  const fetchStandardLos = async () => {
    if ((orgStandardLos && orgStandardLos.length > 0) || !currentOrg?.id) return;
    try {
      const [rsRes, losRes] = await Promise.all([
        fetchRateSheet(currentOrg.id),
        fetchSlLosOptions()
      ]);
      const standard = (rsRes?.rate_sheets || []).find((rs) => rs.is_default);
      if (!standard || !losRes?.success) return;
      const list = (losRes.data || [])
        .filter((o) => String(o.rate_sheet_id) === String(standard.id))
        .map((o) => ({
          id: String(o.id),
          code: o.code,
          name: o.name,
          rate_sheet_id: String(standard.id)
        }));
      if (list.length > 0) setOrgStandardLos(list);
    } catch (err) {
      console.error("Failed to fetch linehaul tariff options:", err);
    }
  };

  const startEditing = () => {
    userTouchedTariffRef.current = false;
    const savedLegs = activeData?.legs || [];
    const terminalDates = buildLegDatesFromTerminals(activeData?.terminals, savedLegs);
    const hasSavedLeg1 = Boolean(
      terminalDates[1]
      || savedLegs[0]?.linehaul_service_date
      || originalLegDatesRef.current[1]
      || originalLegDatesRef.current["1"]
    );
    // Re-run pickup → leg-1 default each time edit opens with no saved service date
    // (e.g. pickup set on details after create, or re-open after cancel).
    if (!hasSavedLeg1) {
      manualFirstLegEditRef.current = false;
      lastPickupSyncedKeyRef.current = null;
      prevPickupDateKeyRef.current = undefined;
    }
    setEditing(true);
    if (lanePathsRef.current.length > 0) {
      applyLanePaths(lanePathsRef.current);
    }
    setEditServiceLeg(activeData.service_date_leg || (legOptions[0] ? legOptions[0].value : null));
    setEditServiceDate(activeData.service_date ? moment(activeData.service_date) : (activeData.date ? moment(activeData.date) : null));
    setEditLhSpecial(resolveLhSpecial());
    const nextTariffId = resolveTariffForDropdown();
    if (nextTariffId) setTariffId(nextTariffId);
    fetchLanes();
    if (!linehaulLosOptionsProp.length) fetchStandardLos();
  };

  const restoreLegDatesFromSnapshot = (snapshot = {}) => {
    const restored = {};
    Object.entries(snapshot).forEach(([key, value]) => {
      const parsed = toStartOfDay(value);
      if (parsed) restored[key] = parsed;
    });
    return restored;
  };

  const cancelEditing = () => {
    userTouchedTariffRef.current = false;
    setEditing(false);
    setEditPathId(resolveTrackingPathId(activeData, currentPathId));
    setEditServiceLeg(activeData.service_date_leg || null);
    setEditServiceDate(activeData.service_date ? moment(activeData.service_date) : (activeData.date ? moment(activeData.date) : null));
    setEditLhSpecial(resolveLhSpecial());
    const nextTariffId = resolveTariffForDropdown();
    if (nextTariffId) setTariffId(nextTariffId);
    manualFirstLegEditRef.current = false;
    lastPickupSyncedKeyRef.current = initialPickupDateKeyRef.current;
    prevPickupDateKeyRef.current = initialPickupDateKeyRef.current ?? undefined;
    setLegDatesMap(restoreLegDatesFromSnapshot(originalLegDatesRef.current));
  };

  const handleSave = () => {
    if (!customerOrderId) return;

    setSaving(true);
    setSavingTip("Saving routing path...");
    const formattedDate = editServiceDate ? editServiceDate.format("YYYY-MM-DD") : null;
    const selectedTariffObj = tariffId
      ? losOptions.find((item) => String(item.id) === String(tariffId))
      : null;
    const tariffData = buildLinehaulTariffPayload(selectedTariffObj);

    const rawPaths = displayLanePaths.length ? displayLanePaths : (activeData?.paths || []);
    const activeLane = rawPaths.find((p) => String(p.path_id || p._id || p.id) === String(editPathId)) || rawPaths[0];
    const legsForSave = resolveLegsForSave(activeLane, activeData, legDatesMap);
    let dateChanged = false;
    const formattedLegs = legsForSave.map((leg, idx) => {
      const connectorIndex = leg.connectorIndex ?? (idx + 1);
      const linehaul_service_date = formatLegDateKey(legDatesMap[connectorIndex]);
      const originalDate = originalLegDatesRef.current[connectorIndex]
        ?? originalLegDatesRef.current[String(connectorIndex)]
        ?? null;
      if (linehaul_service_date !== originalDate) {
        dateChanged = true;
      }

      const { connectorIndex: _drop, ...legFields } = leg;
      return {
        routing_id: String(legFields.routing_id || legFields._id || legFields.id || ""),
        origin_id: String(legFields.origin_id || ""),
        origin_code: String(legFields.origin_code || ""),
        destination_id: String(legFields.destination_id || ""),
        destination_code: String(legFields.destination_code || ""),
        ...(linehaul_service_date ? { linehaul_service_date } : {})
      };
    });

    const linehaulError = getLinehaulRouteValidationError({
      path_id: editPathId,
      legs: formattedLegs,
      tariff: tariffData,
    });
    if (linehaulError) {
      setSaving(false);
      alertMessage(linehaulError, "warning", 10);
      return;
    }

    LineHaulApi.updateLinehaulRoutingLane({
      customer_order_id: customerOrderId,
      lh_routing_path_id: editPathId,
      linehaul_special: Boolean(editLhSpecial),
      ...(dateChanged ? { date_changed: true } : {}),
      linehaul_tracking: {
        path_id: String(editPathId || ""),
        legs: formattedLegs,
        tariff: tariffData,
        ...(formattedDate ? { service_date: formattedDate } : {})
      }
    })
      .then(async (result) => {
        if (result.success) {
          alertMessage(result?.data?.message || "Linehaul routing details updated successfully");
          const savedLosId = extractLinehaulTariffLosId(result?.data) || tariffData?.los_id;
          const savedDropdownId = tariffId
            ? String(tariffId)
            : (savedLosId ? resolveLinehaulTariffIdFromLosId(savedLosId, losOptions) : undefined);
          if (savedLosId) {
            savedTariffLosIdRef.current = String(savedLosId);
            setSavedLinehaulTariffLosId(String(savedLosId));
          }
          if (tariffData) {
            savedTariffMetaRef.current = tariffData;
          }
          if (savedDropdownId) {
            savedTariffDropdownIdRef.current = String(savedDropdownId);
            setTariffId(String(savedDropdownId));
          }
          userTouchedTariffRef.current = false;
          setSavedLhSpecial(
            result?.data?.linehaul_special != null
              ? Boolean(result.data.linehaul_special)
              : Boolean(editLhSpecial)
          );
          originalLegDatesRef.current = snapshotLegDateMap(legDatesMap);
          setEditing(false);
          appliedApiLegDatesRef.current = false;
          setSavingTip("Fetching updated tracking...");
          await waitForTrackingRefresh(3000);
          return fetchRealtimeTracking();
        }
        alertMessage(result?.message || "Something went wrong", "error");
        return null;
      })
      .finally(() => setSaving(false));
  };

  return (
    <div className={`lh-leg-card lh-leg-card-${status}${saving ? " is-saving" : ""}`}>
      {saving && (
        <div className="lh-leg-card-saving" aria-live="polite" aria-busy="true">
          <Spin tip={savingTip} />
        </div>
      )}
      {/* Header Bar */}
      <div className="lh-leg-header">
        <div className="lh-leg-header-title">
          <LinehaulTrackIcon width="24" height="24" />
          <span className="lh-leg-title-text">{title}</span>
          <button
            type="button"
            className={`lh-leg-toggle ${collapsed ? "is-collapsed" : ""}`}
            onClick={() => setCollapsed(!collapsed)}
            aria-label={collapsed ? "Expand leg" : "Collapse leg"}
          >
            <Icon type="caret-down" />
          </button>
        </div>

        <div className="lh-leg-header-actions">
          {editing ? (
            <label className="lh-leg-header-special">
              <Checkbox
                checked={editLhSpecial}
                disabled={saving}
                onChange={(e) => setEditLhSpecial(e.target.checked)}
              />
              Special
            </label>
          ) : isLinehaulSpecial ? (
            <span className="lh-leg-chip lh-leg-chip-special">
              <StarIcon width="16" height="16" color="#1A354D" />
              Special
            </span>
          ) : null}

          {hasRealtimeStops && (
            <span className={`lh-leg-chip lh-leg-chip-${status}`}>
              <PillIcon width="16" height="16" color={color} />
              {label}
            </span>
          )}

          {!editing && hasRealtimeStops && (
            <button
              type="button"
              className="lh-leg-edit"
              onClick={startEditing}
              aria-label="Edit routing path"
            >
              <EditPennIcon width="16" height="16" color="#1A354D" />
            </button>
          )}
        </div>
      </div>

      {/* Top Controls Row: Routing Path (Left) & Tariff (Right) - Rendered ONLY when editing */}
      {!collapsed && editing && (
        <div className="lh-leg-top-controls">
          <div className="lh-leg-field lh-leg-field-routing">
            <label className="lh-leg-field-label">Routing Path</label>
            <div className="lh-leg-routing-input-wrap">
              <Select
                value={selectedPathReady ? String(editPathId) : undefined}
                onChange={(val) => setEditPathId(val != null ? String(val) : null)}
                placeholder={fetchingLanes ? "Loading routes..." : "--Select--"}
                className="lh-tracking-select"
                dropdownClassName="lh-routing-path-dropdown"
                disabled={saving || lastRoutingLegComplete}
                loading={fetchingLanes && !selectedPathReady}
              >
                {displayLanePaths.map((path) => (
                  <Select.Option
                    key={String(path.path_id)}
                    value={String(path.path_id)}
                    title={path.route}
                  >
                    {renderRoutingPathLabel(path.route, dropdownCompletedCodes)}
                  </Select.Option>
                ))}
              </Select>
            </div>
          </div>

          <div className="lh-leg-field lh-leg-field-tariff">
            <label className="lh-leg-field-label">
              <sup className="lh-required-star">*</sup>
              Tariff
            </label>
            <Select_v2
              data-testid="linehaul-tariff-dropdown"
              labelField="name"
              valueField="id"
              codeField="code"
              groupField="los_group"
              options={losOptions}
              value={tariffId != null && tariffId !== "" ? String(tariffId) : undefined}
              onChange={(value) => {
                userTouchedTariffRef.current = true;
                setTariffId(value ? String(value) : undefined);
              }}
              placeholder="--Select--"
              showSelectOption={true}
              className="lh-tracking-select"
              dropdownMatchSelectWidth={false}
              disabled={saving || linehaulTariffEditLocked}
            />
          </div>
        </div>
      )}

      {!collapsed && stops.length === 0 && (
        <div className="lh-leg-empty-path">
          <Icon type="info-circle" />
          <span>
            {editing
              ? "No routing path is available yet. Choose one above to assign it to this order."
              : "No routing path is available for this order yet."}
          </span>
        </div>
      )}

      {/* Timeline Section with Inline Date Selector Pills */}
      {!collapsed && stops.length > 0 && (
        <div className="lh-leg-timeline-scroll">
        <div className="lh-leg-timeline">
          {stops.map((stop, index) => {
            const rawPaths = displayLanePaths.length ? displayLanePaths : (activeData?.paths || []);
            const activeLane = rawPaths.find((p) => String(p.path_id || p._id || p.id) === String(editPathId)) || rawPaths[0];
            const routingLegCount = Math.max((activeLane?.routeCodes?.length || 1) - 1, 0);
            const prefixCount = Math.max(
              (activeLane?.routeCodes?.length || 0)
              - (activeLane?.remainingCodes?.length || activeLane?.routeCodes?.length || 0),
              0
            );
            const remainingLegIndex = index - prefixCount;
            const timelineKey = resolveTimelineLegDateKey(index, stops, orderLabel, routingLegCount);
            const legDateKey = remainingLegIndex >= 0 && timelineKey != null
              ? remainingLegIndex + 1
              : null;
            const isIntermediateLeg = shouldShowLegDatePill(
              index,
              stops.length,
              orderLabel,
              stops,
              routingLegCount
            ) && timelineKey != null;
            const legData = (legDateKey != null
              ? activeLane?.legs?.[legDateKey - 1]
              : activeLane?.legs?.[index - 1])
              || activeData?.legs?.[(legDateKey != null ? legDateKey : index) - 1]
              || {};
            const operationalDays = legData.operational_days || [];
            const sortedTerminals = [...(activeData?.terminals || [])].sort(
              (a, b) => (a.sequence_no ?? 0) - (b.sequence_no ?? 0)
            );
            const fullLegKey = prefixCount + (legDateKey || 0);
            const apiServiceDate = (fullLegKey > 0
              ? resolveLegLinehaulServiceDate(sortedTerminals, fullLegKey, legData)
              : null)
              || legData.linehaul_service_date
              || null;
            const mappedLegDate = legDateKey != null ? legDatesMap[legDateKey] : null;
            const pickupRevised = Boolean(
              initialPickupDateKeyRef.current
              && pickupDateKey
              && pickupDateKey !== initialPickupDateKeyRef.current
            );
            const pickupFirstLegFallback = editing && legDateKey === 1 && pickupDateMoment && pickupRevised
              ? resolveFirstLegDateFromPickup(pickupDateMoment, legData)
              : null;
            const currentLegDate = editing
              ? (mappedLegDate || pickupFirstLegFallback || toStartOfDay(apiServiceDate))
              : toStartOfDay(apiServiceDate);

            const datePill = isIntermediateLeg ? (
              <LegDatePillPicker
                value={currentLegDate}
                disabled={!editing || saving || isLegDateLocked(stop)}
                operationalDays={operationalDays}
                minDate={pickupDateMoment}
                onChange={(newDate) => {
                  const storeKey = legDateKey != null ? legDateKey : index;
                  const currentKey = formatLegDateKey(legDateKey != null ? legDatesMap[legDateKey] : null);
                  const newKey = formatLegDateKey(newDate);
                  if (newKey === currentKey) return;

                  if (storeKey === 1) {
                    manualFirstLegEditRef.current = true;
                    lastPickupSyncedKeyRef.current = null;
                  }
                  setLegDatesMap((prev) => {
                    const next = { ...prev };
                    next[storeKey] = newDate;
                    return next;
                  });
                }}
              />
            ) : null;

            return (
              <React.Fragment key={stop.id || `${stop.name}-${index}`}>
                <Stop
                  stop={stop}
                  align={index === 0 ? "start" : index === stops.length - 1 ? "end" : "center"}
                />
                {index < stops.length - 1 && (
                  <LegConnector leg={stop.leg} datePill={datePill} />
                )}
              </React.Fragment>
            );
          })}
        </div>
        </div>
      )}

      {/* Action Buttons (Bottom Right) */}
      {editing && (
        <div className="lh-leg-edit-actions">
          <button type="button" className="lh-leg-btn-cancel" onClick={cancelEditing} disabled={saving}>
            Cancel
          </button>
          <button type="button" className="lh-leg-btn-save" onClick={handleSave} disabled={saving}>
            {saving ? "Saving..." : "Save"}
          </button>
        </div>
      )}
    </div>
  );
};

export default LhTrackingCard;
